import { GripVertical, Plus, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { SpecColorsEditor } from './SpecColorsEditor';
import { SpecDecorationsEditor } from './SpecDecorationsEditor';
import { SpecValueInput } from './SpecValueInput';
import { SPEC_FIELDS, getSpecField, type SpecFieldGroup } from '@codezo/product-specs';
import {
	countSpecEntries,
	makeSpecCustomRow,
	makeSpecFieldRow,
	type SpecFormState,
} from '@codezo/product-specs';

const GROUP_LABELS: Record<SpecFieldGroup, string> = {
	material: 'Material & finish',
	packaging: 'Packaging',
};

const FIELD_ORDER = new Map(SPEC_FIELDS.map((def, i) => [def.key, i]));

/* ── Row shell ── */

function SpecRow({
	label,
	children,
	onRemove,
	dragHandle,
	dragging,
	disabled,
	...dropProps
}: {
	label: React.ReactNode;
	children: React.ReactNode;
	onRemove: () => void;
	dragHandle?: React.ReactNode;
	dragging?: boolean;
	disabled?: boolean;
} & Pick<
	React.ComponentProps<'div'>,
	'onDragOver' | 'onDrop' | 'draggable' | 'onDragStart' | 'onDragEnd'
>) {
	return (
		<div
			{...dropProps}
			className={cn(
				'flex flex-col gap-2 rounded-lg px-1 py-1 transition-colors sm:flex-row sm:items-start',
				dragging && 'opacity-40'
			)}
		>
			<div className="flex w-4 shrink-0 items-center justify-center self-center sm:h-8 sm:self-start">
				{dragHandle}
			</div>
			<div className="w-full shrink-0 sm:w-[210px]">{label}</div>
			<div className="min-w-0 flex-1">{children}</div>
			<button
				type="button"
				onClick={onRemove}
				disabled={disabled}
				className="flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center self-end rounded-md text-text-4 transition-colors hover:bg-surface-hover hover:text-destructive disabled:cursor-default disabled:opacity-50 sm:self-start"
				aria-label="Remove field"
			>
				<Trash2 size={14} />
			</button>
		</div>
	);
}

/* ════════════════════════ EDITOR ════════════════════════ */

/**
 * Flat label/value editor over `ProductSpecificationsInput`. Typed fields come
 * from the registry and stay in registry order; anything the registry doesn't
 * cover becomes a reorderable custom field, so an unforeseen spec never needs
 * a schema change.
 */
export function SpecificationsEditor({
	value,
	onChange,
	disabled,
}: {
	value: SpecFormState;
	onChange: (next: SpecFormState) => void;
	disabled?: boolean;
}) {
	const [dragUid, setDragUid] = useState<string | null>(null);
	/* A row is only `draggable` once its handle is pressed, so the inputs stay
	 * selectable the rest of the time. */
	const [handleArmed, setHandleArmed] = useState<string | null>(null);

	/* Releasing without dragging disarms; a real drag suppresses mouseup and is
	 * disarmed by onDragEnd instead. */
	useEffect(() => {
		if (!handleArmed) return;
		const disarm = () => setHandleArmed(null);
		document.addEventListener('mouseup', disarm);
		return () => document.removeEventListener('mouseup', disarm);
	}, [handleArmed]);

	const present = new Set(value.fields.map((f) => f.key));
	const missing = SPEC_FIELDS.filter((def) => !present.has(def.key));

	const sortedFields = [...value.fields].sort(
		(a, b) => (FIELD_ORDER.get(a.key) ?? 0) - (FIELD_ORDER.get(b.key) ?? 0)
	);

	const addField = (key: string) =>
		onChange({ ...value, fields: [...value.fields, makeSpecFieldRow(key)] });

	const removeField = (key: string) =>
		onChange({ ...value, fields: value.fields.filter((f) => f.key !== key) });

	/* Reorder while dragging so the list previews the drop position. */
	const moveCustom = (fromUid: string, toUid: string) => {
		if (fromUid === toUid) return;
		const from = value.custom.findIndex((c) => c.uid === fromUid);
		const to = value.custom.findIndex((c) => c.uid === toUid);
		if (from === -1 || to === -1) return;
		const next = [...value.custom];
		const [moved] = next.splice(from, 1);
		next.splice(to, 0, moved);
		onChange({ ...value, custom: next });
	};

	const entryCount = countSpecEntries(value);
	let renderedGroup: SpecFieldGroup | null = null;

	return (
		<div className="flex flex-col gap-4">
			{/* ── Typed + custom fields ── */}
			<div className="flex flex-col gap-1">
				{sortedFields.map((row) => {
					const def = getSpecField(row.key);
					if (!def) return null;
					const heading = def.group !== renderedGroup ? GROUP_LABELS[def.group] : null;
					renderedGroup = def.group;

					return (
						<div key={row.key}>
							{heading && (
								<div className="px-1 pt-3 pb-1.5 text-2xs uppercase tracking-[0.08em] text-text-4">
									{heading}
								</div>
							)}
							<SpecRow
								label={
									<div className="flex h-8 items-center rounded-lg border border-line-1 bg-surface-fill/60 px-2.5 text-sm text-text-2">
										<span className="truncate">{def.label}</span>
									</div>
								}
								onRemove={() => removeField(row.key)}
								disabled={disabled}
							>
								<SpecValueInput
									row={row}
									onChange={(next) =>
										onChange({
											...value,
											fields: value.fields.map((f) =>
												f.key === row.key ? next : f
											),
										})
									}
									disabled={disabled}
								/>
							</SpecRow>
						</div>
					);
				})}

				{value.custom.length > 0 && (
					<div className="px-1 pt-3 pb-1.5 text-2xs uppercase tracking-[0.08em] text-text-4">
						Custom
					</div>
				)}

				{value.custom.map((row) => (
					<SpecRow
						key={row.uid}
						draggable={handleArmed === row.uid && !disabled}
						onDragStart={() => setDragUid(row.uid)}
						onDragEnd={() => {
							setDragUid(null);
							setHandleArmed(null);
						}}
						onDragOver={(e) => {
							if (!dragUid) return;
							e.preventDefault();
							moveCustom(dragUid, row.uid);
						}}
						onDrop={(e) => e.preventDefault()}
						dragging={dragUid === row.uid}
						dragHandle={
							<span
								onMouseDown={() => !disabled && setHandleArmed(row.uid)}
								className={cn(
									'flex cursor-grab items-center text-text-4 transition-colors hover:text-text-2 active:cursor-grabbing',
									disabled && 'pointer-events-none opacity-50'
								)}
							>
								<GripVertical size={14} />
							</span>
						}
						label={
							<Input
								value={row.label}
								onChange={(e) =>
									onChange({
										...value,
										custom: value.custom.map((c) =>
											c.uid === row.uid ? { ...c, label: e.target.value } : c
										),
									})
								}
								placeholder="Field name"
								maxLength={60}
								disabled={disabled}
							/>
						}
						onRemove={() =>
							onChange({
								...value,
								custom: value.custom.filter((c) => c.uid !== row.uid),
							})
						}
						disabled={disabled}
					>
						<div className="flex items-center gap-2">
							<Input
								value={row.value}
								onChange={(e) =>
									onChange({
										...value,
										custom: value.custom.map((c) =>
											c.uid === row.uid ? { ...c, value: e.target.value } : c
										),
									})
								}
								placeholder="Value"
								maxLength={200}
								disabled={disabled}
							/>
							<Input
								value={row.unit}
								onChange={(e) =>
									onChange({
										...value,
										custom: value.custom.map((c) =>
											c.uid === row.uid ? { ...c, unit: e.target.value } : c
										),
									})
								}
								placeholder="Unit"
								maxLength={16}
								disabled={disabled}
								className="w-[90px] shrink-0"
							/>
						</div>
					</SpecRow>
				))}

				<button
					type="button"
					disabled={disabled}
					onClick={() =>
						onChange({ ...value, custom: [...value.custom, makeSpecCustomRow()] })
					}
					className="mt-1 flex w-fit cursor-pointer items-center gap-1.5 rounded-md px-1.5 py-1.5 text-sm text-text-3 transition-colors hover:bg-surface-hover hover:text-text-1 disabled:cursor-default disabled:opacity-50"
				>
					<Plus size={14} />
					Add field
				</button>
			</div>

			{/* ── Quick-add palette for the fields not on the product yet ── */}
			{missing.length > 0 && (
				<div className="flex flex-wrap items-center gap-1.5 border-t border-line-1 pt-3">
					<span className="mr-1 text-2xs uppercase tracking-[0.08em] text-text-4">
						Common
					</span>
					{missing.map((def) => (
						<button
							key={def.key}
							type="button"
							disabled={disabled}
							onClick={() => addField(def.key)}
							className="flex cursor-pointer items-center gap-1 rounded-full border border-line-2 px-2.5 py-1 text-xs text-text-2 transition-colors hover:border-line-3 hover:text-text-1 disabled:cursor-default disabled:opacity-50"
						>
							<Plus size={11} className="text-text-4" />
							{def.label}
						</button>
					))}
				</div>
			)}

			<div className="border-t border-line-1 pt-4">
				<SpecColorsEditor
					value={value.colors}
					onChange={(colors) => onChange({ ...value, colors })}
					disabled={disabled}
				/>
			</div>

			<div className="border-t border-line-1 pt-4">
				<SpecDecorationsEditor
					value={value.decorations}
					onChange={(decorations) => onChange({ ...value, decorations })}
					disabled={disabled}
				/>
			</div>

			<p className="text-xs text-text-4">
				{entryCount} {entryCount === 1 ? 'specification' : 'specifications'} · saved with
				the product
			</p>
		</div>
	);
}
